import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPasswordSchema } from '@daater/shared';
import { Button } from '@daater/ui';
import { useResetPasswordMutation } from '../api/queries';

type ResetInput = { password: string; repassword: string };

export default function PasswordRecoveryConfirmScreen() {
  const navigate = useNavigate();
  const token =
    typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('token') ?? '')
      : '';
  const resetMutation = useResetPasswordMutation();
  const [done, setDone] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetInput>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (values: ResetInput) => {
    setErrorMessage(null);
    try {
      await resetMutation.mutateAsync({
        token,
        password: values.password,
        repassword: values.repassword,
      });
      setDone(true);
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-pale-grey px-4">
      <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-charcoal-grey">Restablecer contraseña</h1>
        <p className="mt-1 text-sm text-slate">Ingresa una nueva contraseña para tu cuenta.</p>

        {!token ? (
          <div className="mt-6 rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-3 text-sm text-darkish-pink">
            Token inválido o expirado.
          </div>
        ) : done ? (
          <div className="mt-6 rounded-md border border-greenish-teal/30 bg-greenish-teal/10 px-3 py-3 text-sm text-greenish-teal">
            Contraseña actualizada. Ahora puedes iniciar sesión.
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={event => void handleSubmit(onSubmit)(event)}>
            <div>
              <label className="text-xs font-semibold uppercase text-slate">Nueva contraseña</label>
              <input
                type="password"
                {...register('password')}
                className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                placeholder="••••••••"
              />
              {errors.password ? (
                <p className="mt-1 text-xs text-darkish-pink">{errors.password.message}</p>
              ) : null}
            </div>

            <div>
              <label className="text-xs font-semibold uppercase text-slate">
                Confirmar contraseña
              </label>
              <input
                type="password"
                {...register('repassword')}
                className="mt-1 w-full rounded-md border border-pale-grey-three px-3 py-2 text-sm"
                placeholder="••••••••"
              />
              {errors.repassword ? (
                <p className="mt-1 text-xs text-darkish-pink">{errors.repassword.message}</p>
              ) : null}
            </div>

            <Button type="submit" className="w-full" disabled={!token || resetMutation.isPending}>
              {resetMutation.isPending ? 'Actualizando...' : 'Actualizar'}
            </Button>
            {errorMessage ? <p className="text-xs text-darkish-pink">{errorMessage}</p> : null}
          </form>
        )}

        <button
          type="button"
          onClick={() => void navigate({ to: '/login' })}
          className="mt-4 text-xs text-slate underline"
        >
          Volver al login
        </button>
      </div>
    </div>
  );
}
